import axiosClass from '@/../util/axiosClass';

/**
 * 연차 관련 API 서비스 클래스
 * @description 연차 데이터 조회 및 관리를 위한 서비스 계층
 */
class VacationService {
    constructor() {
        this.cache = new Map();
        this.cacheTimeout = 5 * 60 * 1000; // 5분 캐시
        this.pendingRequests = new Map();
    }

    /**
     * 캐시 키 생성
     * @param {string} method - 메서드명
     * @param {Object} params - 파라미터
     * @returns {string} 캐시 키
     */
    generateCacheKey(method, params) {
        return `${method}_${JSON.stringify(params)}`;
    }

    /**
     * 캐시에서 데이터 조회
     * @param {string} key - 캐시 키
     * @returns {*} 캐시된 데이터 또는 null
     */
    getFromCache(key) {
        const cached = this.cache.get(key);
        if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
            return cached.data;
        }
        this.cache.delete(key);
        return null;
    }

    /**
     * 캐시에 데이터 저장
     * @param {string} key - 캐시 키
     * @param {*} data - 저장할 데이터
     */
    setCache(key, data) {
        this.cache.set(key, {
            data,
            timestamp: Date.now()
        });
    }

    /**
     * 특정 담당자들의 날짜 범위 연차 조회
     * @param {string} startDate - 시작일 (YYYY-MM-DD 또는 YYYYMMDD)
     * @param {string} endDate - 종료일 (YYYY-MM-DD 또는 YYYYMMDD)
     * @param {Array<string>} managerUids - 담당자 UID 배열
     * @returns {Promise<Map<string, Array>>} 날짜별 연차 Map
     */
    async getVacationsByManagers(startDate, endDate, managerUids) {
        if (!managerUids || managerUids.length === 0) {
            return new Map();
        }

        const cacheKey = this.generateCacheKey('vacationsByManagers', { startDate, endDate, managerUids });

        // 캐시 확인
        const cachedData = this.getFromCache(cacheKey);
        if (cachedData) {
            return cachedData;
        }

        // 중복 요청 방지
        if (this.pendingRequests.has(cacheKey)) {
            return await this.pendingRequests.get(cacheKey);
        }

        const requestPromise = this.fetchVacationsByManagers(startDate, endDate, managerUids);
        this.pendingRequests.set(cacheKey, requestPromise);

        try {
            const result = await requestPromise;
            this.setCache(cacheKey, result);
            return result;
        } finally {
            this.pendingRequests.delete(cacheKey);
        }
    }

    /**
     * 담당자 연차 데이터 API 호출
     * @private
     */
    async fetchVacationsByManagers(startDate, endDate, managerUids) {
        try {
            // 날짜 형식 정규화 (YYYYMMDD -> YYYY-MM-DD)
            const formattedStartDate = this.formatDateForApi(startDate);
            const formattedEndDate = this.formatDateForApi(endDate);

            const { data } = await axiosClass.get('/api/vacation/byManagers', {
                startDate: formattedStartDate,
                endDate: formattedEndDate,
                managerUids: managerUids.join(',')
            }, true);

            return this.transformToDateMap(data.list || []);
        } catch (error) {
            console.error('연차 정보 조회 실패:', error);
            // 에러 시 빈 Map 반환 (UI가 깨지지 않도록)
            return new Map();
        }
    }

    /**
     * 날짜 범위로 연차 조회 (기존 API 활용)
     * @param {string} startDate - 시작일
     * @param {string} endDate - 종료일
     * @returns {Promise<Map<string, Array>>} 날짜별 연차 Map
     */
    async getVacationsByDateRange(startDate, endDate) {
        const cacheKey = this.generateCacheKey('vacationsByDateRange', { startDate, endDate });

        const cachedData = this.getFromCache(cacheKey);
        if (cachedData) {
            return cachedData;
        }

        try {
            const formattedStartDate = this.formatDateForApi(startDate);
            const formattedEndDate = this.formatDateForApi(endDate);

            const { data } = await axiosClass.get('/api/vacation/checkRange', {
                startDate: formattedStartDate,
                endDate: formattedEndDate
            }, true);

            const result = this.transformToDateMap(data.list || []);
            this.setCache(cacheKey, result);
            return result;
        } catch (error) {
            console.error('연차 정보 조회 실패:', error);
            return new Map();
        }
    }

    /**
     * API 응답을 날짜별 Map으로 변환
     * @param {Array} vacationList - 연차 목록
     * @returns {Map<string, Array>} 날짜별 연차 Map
     */
    transformToDateMap(vacationList) {
        const map = new Map();

        if (!Array.isArray(vacationList)) {
            return map;
        }

        vacationList.forEach(vacation => {
            // VACATION_DATE 형식: YYYY-MM-DD 또는 YYYY-MM-DDTHH:mm:ss
            const dateKey = this.extractDateKey(vacation.VACATION_DATE);

            if (!map.has(dateKey)) {
                map.set(dateKey, []);
            }

            map.get(dateKey).push({
                staffUid: vacation.STAFF_UID,
                staffName: vacation.STAFF_NAME || vacation.MEMBER_USERNAME || '',
                vacationType: vacation.VACATION_TYPE,
                memo: vacation.MEMO || '',
                medicalName: vacation.MEDICAL_NM || ''
            });
        });

        return map;
    }

    /**
     * 날짜 문자열에서 YYYYMMDD 키 추출
     * @param {string} dateString - 날짜 문자열
     * @returns {string} YYYYMMDD 형식의 키
     */
    extractDateKey(dateString) {
        if (!dateString) return '';

        // ISO 형식(UTC)인 경우 한국 시간으로 변환
        if (dateString.includes('T') || dateString.includes('Z')) {
            const date = new Date(dateString);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}${month}${day}`;
        }

        // YYYY-MM-DD 형식 처리
        const dateOnly = dateString.substring(0, 10);
        return dateOnly.replace(/-/g, '');
    }

    /**
     * API용 날짜 형식으로 변환
     * @param {string} date - 날짜 문자열 (YYYYMMDD 또는 YYYY-MM-DD)
     * @returns {string} YYYY-MM-DD 형식
     */
    formatDateForApi(date) {
        if (!date) return '';

        // 이미 YYYY-MM-DD 형식이면 그대로 반환
        if (date.includes('-')) {
            return date.substring(0, 10);
        }

        // YYYYMMDD -> YYYY-MM-DD
        if (date.length === 8) {
            return `${date.substring(0, 4)}-${date.substring(4, 6)}-${date.substring(6, 8)}`;
        }

        return date;
    }

    /**
     * 특정 날짜의 연차 목록 조회 (Map에서)
     * @param {Map} vacationMap - 연차 Map
     * @param {string} dateKey - 날짜 키 (YYYYMMDD)
     * @returns {Array} 해당 날짜의 연차 목록
     */
    getVacationsForDate(vacationMap, dateKey) {
        const normalizedKey = dateKey.replace(/-/g, '');
        return vacationMap.get(normalizedKey) || [];
    }

    /**
     * 캐시 무효화
     * @param {string} type - 무효화 타입 ('all' | 'byManagers' | 'byDateRange')
     */
    clearCache(type = 'all') {
        if (type === 'all') {
            this.cache.clear();
        } else {
            for (const key of this.cache.keys()) {
                if (key.startsWith(type)) {
                    this.cache.delete(key);
                }
            }
        }
    }

    /**
     * 캐시 상태 조회
     * @returns {Object} 캐시 상태 정보
     */
    getCacheStats() {
        return {
            size: this.cache.size,
            keys: Array.from(this.cache.keys())
        };
    }
}

// 싱글톤 인스턴스 생성 및 내보내기
const vacationService = new VacationService();

export default vacationService;
